// sections/@dashboard/finance/FinanceTableRow.tsx
// COMPLETE UPDATED VERSION — adds working audit trail dialog
import { useState } from "react";
import {
  TableRow,
  TableCell,
  Typography,
  Chip,
  Stack,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { fCurrency } from "../../../utils/formatNumber";
import { fDate, fDateTime } from "../../../utils/formatTime";
import Iconify from "../../../components/iconify";
import {
  IBankTransaction,
  ITransactionCategory,
  ICategoryAuditEntry,
} from "../../../@types/finance";
import { FinanceService } from "../../../api/finance.service";

const SOURCE_BADGE: Record<string, { label: string; bg: string; color: string } | undefined> = {
  till_payment: { label: 'Till', bg: '#ecfdf5', color: '#065f46' },
  till_refund: { label: 'Refund', bg: '#fff7ed', color: '#9a3412' },
};

const TX_TYPE_STYLE: Record<string, { bg: string; color: string }> = {
  CR: { bg: "#dcfce7", color: "#15803d" },
  BP: { bg: "#f3f4f6", color: "#374151" },
  DD: { bg: "#fef3c7", color: "#92400e" },
  VIS: { bg: "#ede9fe", color: "#5b21b6" },
  TFR: { bg: "#e0f2fe", color: "#0369a1" },
  DR: { bg: "#fee2e2", color: "#991b1b" },
};

const VAT_STYLE: Record<string, { bg: string; color: string }> = {
  S: { bg: "#dbeafe", color: "#1d4ed8" },
  E: { bg: "#dcfce7", color: "#15803d" },
  Z: { bg: "#f3f4f6", color: "#374151" },
  N: { bg: "#fef9c3", color: "#854d0e" },
};

const CAT_COLOR: Record<
  string,
  "success" | "error" | "secondary" | "warning" | "default"
> = {
  income: "success",
  expenditure: "error",
  finance: "secondary",
};

interface Props {
  row: IBankTransaction;
  categories: ITransactionCategory[];
  onAssignCategory: () => void;
}

export default function FinanceTableRow({
  row,
  categories,
  onAssignCategory,
}: Props) {
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLog, setAuditLog] = useState<ICategoryAuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const isCredit = row.direction === "credit";
  const isUnclassified = !row.categoryId;
  const txStyle =
    TX_TYPE_STYLE[row.transactionType ?? "BP"] ?? TX_TYPE_STYLE.BP;
  const categoryObj = categories.find((c) => c.id === row.categoryId);
  const sourceBadge = SOURCE_BADGE[row.source];

  const handleViewAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const res = await new FinanceService().getTransactionAudit(
        String(row.id),
      );
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
      setAuditLog(data);
    } catch {
      setAuditLog([]);
    } finally {
      setAuditLoading(false);
    }
  };

  // Derive VAT code from the category
  const vatCode = categoryObj?.vatCode;
  const vatStyle = vatCode ? VAT_STYLE[vatCode] : null;

  return (
    <>
      <TableRow
        hover
        sx={{
          bgcolor: isUnclassified ? "warning.lighter" : "inherit",
          "&:hover": { bgcolor: isUnclassified ? "#fffbeb" : "action.hover" },
        }}>
        {/* Date */}
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="caption" color="text.secondary">
            {fDate(row.transactionDate)}
          </Typography>
        </TableCell>

        {/* Narrative */}
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Tooltip title={row.narrative}>
              <Typography
                variant="body2"
                noWrap
                sx={{ maxWidth: 220, fontWeight: isUnclassified ? 500 : 400 }}>
                {row.narrative}
              </Typography>
            </Tooltip>
            {sourceBadge && (
              <Typography
                variant="caption"
                sx={{
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 0.75,
                  bgcolor: sourceBadge.bg,
                  color: sourceBadge.color,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                {sourceBadge.label}
              </Typography>
            )}
          </Stack>
          {row.counterparty && row.counterparty !== row.narrative && (
            <Typography
              variant="caption"
              color="text.disabled"
              noWrap
              display="block">
              {row.counterparty}
            </Typography>
          )}
        </TableCell>

        {/* Type */}
        <TableCell align="center">
          <Typography
            variant="caption"
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 0.75,
              bgcolor: txStyle.bg,
              color: txStyle.color,
              fontWeight: 600,
              fontFamily: "monospace",
            }}>
            {row.transactionType ?? "—"}
          </Typography>
        </TableCell>

        {/* Amount */}
        <TableCell align="right">
          <Typography
            variant="body2"
            fontWeight={500}
            fontFamily="monospace"
            color={isCredit ? "success.dark" : "error.dark"}>
            {isCredit ? "+" : "−"}
            {fCurrency(Math.abs(row.amount))}
          </Typography>
        </TableCell>

        {/* Category */}
        <TableCell>
          {isUnclassified ? (
            <Chip
              label="Unclassified"
              size="small"
              color="warning"
              variant="soft"
              icon={<Iconify icon="eva:edit-fill" width={12} />}
              onClick={onAssignCategory}
              sx={{ cursor: "pointer" }}
            />
          ) : categoryObj ? (
            <Chip
              label={categoryObj.name}
              size="small"
              color={CAT_COLOR[categoryObj.type] ?? "default"}
              variant="soft"
            />
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          )}
        </TableCell>

        {/* Method */}
        <TableCell align="center">
          {row.categoryMethod ? (
            <Chip
              label={row.categoryMethod === "rule_auto" ? "auto" : "manual"}
              size="small"
              color={row.categoryMethod === "rule_auto" ? "info" : "warning"}
              variant="soft"
              sx={{ fontSize: 10, height: 20 }}
            />
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          )}
        </TableCell>

        {/* VAT code */}
        <TableCell align="center">
          {vatStyle && vatCode ? (
            <Typography
              variant="caption"
              sx={{
                px: 0.75,
                py: 0.2,
                borderRadius: 0.75,
                bgcolor: vatStyle.bg,
                color: vatStyle.color,
                fontWeight: 700,
                fontFamily: "monospace",
              }}>
              {vatCode}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          )}
        </TableCell>

        {/* Status */}
        <TableCell align="center">
          <Chip
            label={row.reconciliationStatus ?? "pending"}
            size="small"
            color={
              row.reconciliationStatus === "reconciled"
                ? "success"
                : row.reconciliationStatus === "unmatched"
                  ? "error"
                  : "warning"
            }
            variant="soft"
            sx={{ textTransform: "capitalize", fontSize: 11 }}
          />
        </TableCell>

        {/* Actions */}
        <TableCell align="center">
          <Stack direction="row" justifyContent="center" spacing={0.5}>
            {/* Assign / recode */}
            <Tooltip
              title={isUnclassified ? "Assign category" : "Recode category"}>
              <IconButton size="small" onClick={onAssignCategory}>
                <Iconify icon="eva:edit-fill" width={15} />
              </IconButton>
            </Tooltip>
            {/* Audit trail — only shows if manually categorised */}
            {row.categoryMethod === "user_manual" && (
              <Tooltip title="View audit trail">
                <IconButton size="small" onClick={handleViewAudit}>
                  <Iconify icon="eva:clock-fill" width={15} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* ── Audit trail dialog ── */}
      <TableRow sx={{ display: "contents" }}>
        <TableCell sx={{ p: 0, border: 0 }} colSpan={9}>
          <Dialog
            open={auditOpen}
            onClose={() => setAuditOpen(false)}
            maxWidth="sm"
            fullWidth>
            <DialogTitle>
              <Stack spacing={0.25}>
                <Typography variant="subtitle1">Audit Trail</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {row.narrative}
                </Typography>
              </Stack>
            </DialogTitle>

            <DialogContent dividers>
              {auditLoading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : auditLog.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Iconify
                    icon="eva:clock-fill"
                    width={36}
                    sx={{
                      color: "text.disabled",
                      display: "block",
                      mx: "auto",
                      mb: 1,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    No audit entries yet
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Category changes will appear here
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {auditLog.map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "grey.50",
                        border: "1px solid",
                        borderColor: "grey.100",
                      }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.75}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.75}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              bgcolor: "primary.lighter",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color="primary.dark">
                              {(entry.changedByUser?.firstName ??
                                "U")[0].toUpperCase()}
                            </Typography>
                          </Box>
                          <Typography variant="caption" fontWeight={600}>
                            {entry.changedByUser?.firstName ??
                              entry.changedByUser?.username ??
                              `User #${entry.changedBy}`}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.disabled">
                          {fDateTime(entry.changedAt)}
                        </Typography>
                      </Stack>

                      <Typography variant="caption" color="text.secondary">
                        {entry.oldCategory ? (
                          <>
                            Changed <strong>{entry.oldCategory.name}</strong> →{" "}
                            <strong>{entry.newCategory?.name}</strong>
                          </>
                        ) : (
                          <>
                            Assigned{" "}
                            <strong>
                              {entry.newCategory?.name ?? "category"}
                            </strong>
                          </>
                        )}
                      </Typography>

                      {entry.reason && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          display="block"
                          sx={{ mt: 0.5, fontStyle: "italic" }}>
                          "{entry.reason}"
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setAuditOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </TableCell>
      </TableRow>
    </>
  );
}
